import { AppConfig } from '@/config/app.config';
import useMediaQuery from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import ESearchInput from '@/components/everridge/ESearchInput';
import { Button } from '@/components/ui/button';
import AddIcon from '@/components/icons/AddIcon';
import { useReleasesFilter } from './store/useReleasesFilter';
import { useReleasesListApi } from './hooks/useReleasesListApi';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import useReleaseListColumns from './hooks/useReleasesListColumns';
import { useState } from 'react';
import ReleaseManage from './components/ReleaseManage';

const ReleasePage = () => {
    const [releaseId, setReleaseId] = useState('');
    const [showReleaseManage, setShowReleaseManage] = useState(false);

    const { releasesListData, isLoadingReleasesListData } = useReleasesListApi();

    const { t } = useTranslation();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);

    const { filter, setFilter } = useReleasesFilter();

    const columns = useReleaseListColumns();

    const handleSearchText = (text: string) => {
        setFilter({ page: 0, searchText: text });
    };

    const handlePageChange = (newPage: number) => {
        setFilter({
            skip: filter.take * newPage,
            page: newPage,
        });
    };

    const handleShowReleaseManage = (id?: string) => {
        if (id) setReleaseId(id);
        else setReleaseId('');
        setShowReleaseManage(true);
    };

    return (
        <section className={cn('flex-1 flex flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader
                title={t('main.releases.title')}
                search={
                    <ESearchInput
                        value={filter.searchText}
                        setValue={handleSearchText}
                        placeholder={t('main.releases.components.search.placeholder')}
                        containerClassName={cn(!isMobile && 'w-60')}
                    />
                }
            >
                <Button
                    variant="outline"
                    size={isMobile ? 'icon' : 'default'}
                    onClick={() => handleShowReleaseManage()}
                >
                    <AddIcon />
                    {!isMobile && t('elements.buttons.addRelease')}
                </Button>
            </EPageHeader>
            <div className={cn('h-full flex flex-col gap-6', isMobile && 'gap-4')}>
                <ETableWithApiPagination
                    columns={columns}
                    data={releasesListData}
                    paginationStep={filter.take}
                    page={filter.page}
                    setPage={handlePageChange}
                    isLoadingPage={isLoadingReleasesListData}
                    showDetails={handleShowReleaseManage}
                />
            </div>
            {showReleaseManage && <ReleaseManage id={releaseId} onClose={setShowReleaseManage} />}
        </section>
    );
};

export default ReleasePage;
